// client/src/pages/auth/Register.jsx
import React, { useRef, useState, useEffect, useContext } from "react";
import "./signup.css";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate, Link, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { axiosInstance } from "../../config";
import { loginCall } from "../../apiCalls";

export default function Register() {
  const usernameRef = useRef();
  const emailRef = useRef();
  const passwordRef = useRef();
  const birthdayMonthRef = useRef();
  const birthdayDayRef = useRef();
  const birthdayYearRef = useRef();

  const navigate = useNavigate();
  const { username: initialUsername } = useParams();
  const { user: authUser, dispatch } = useContext(AuthContext);

  const [msg, setMsg] = useState("");
  const [progress, setProgress] = useState(0);

  // controlled for validation/availability
  const [username, setUsername] = useState(initialUsername || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [errors, setErrors] = useState({ username: "", email: "", password: "", birthday: "" });
  const [usernameAvail, setUsernameAvail] = useState({ checking: false, available: null });
  const [emailAvail, setEmailAvail] = useState({ checking: false, available: null });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const days = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0"));

  useEffect(() => {
    if (initialUsername) setUsername(initialUsername);
  }, [initialUsername]);

  /* ---------------- validation ---------------- */
  const USERNAME_MIN = 3;
  const USERNAME_MAX = 39;
  const USERNAME_RE = /^[A-Za-z0-9_]+$/;

  const validateUsername = (v) => {
    if (!v) return "";
    if (v.length < USERNAME_MIN) return `Username must be at least ${USERNAME_MIN} characters.`;
    if (v.length > USERNAME_MAX) return `Username must be at most ${USERNAME_MAX} characters.`;
    if (!USERNAME_RE.test(v)) return "Only letters, numbers, and underscores are allowed. No spaces.";
    return "";
  };
  const validateEmail = (v) => {
    if (!v) return "";
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "" : "Please enter a valid email address.";
  };
  const validatePassword = (v) => (!v ? "Password is required." : v.length < 3 ? "Password must be at least 3 characters." : "");
  const validateBirthday = () => {
    const mm = birthdayMonthRef.current?.value;
    const dd = birthdayDayRef.current?.value;
    const yy = birthdayYearRef.current?.value;
    return !mm || !dd || !yy ? "Please select your full birthday." : "";
  };

  /* ---------- helpers for robust availability parsing ---------- */
  const parseAvailable = (data) => {
    // accept many server shapes
    if (!data) return null;
    if (typeof data.available === "boolean") return data.available;
    if (typeof data.exists === "boolean") return !data.exists;
    if (typeof data.taken === "boolean") return !data.taken;
    if (typeof data.inUse === "boolean") return !data.inUse;
    return null; // unknown shape -> don’t assert
  };

  const safeCheck = async (path, params) => {
    // bounded request that always settles
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 3000); // 3s hard timeout
    try {
      const res = await axiosInstance.get(path, { params, signal: ctrl.signal, timeout: 3500 });
      return { ok: true, data: res?.data };
    } catch (err) {
      return { ok: false, error: err };
    } finally {
      clearTimeout(timer);
    }
  };

  /* ---------------- username availability (debounced) ---------------- */
  useEffect(() => {
    const baseErr = validateUsername(username);
    setErrors((e) => ({ ...e, username: baseErr }));
    setUsernameAvail({ checking: false, available: null });
    if (baseErr || !username) return;

    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      setUsernameAvail({ checking: true, available: null });

      const { ok, data } = await safeCheck("/auth/check-username", { username });
      if (cancelled) return;

      const available = parseAvailable(data);
      setUsernameAvail({ checking: false, available });

      if (ok && available === false) {
        setErrors((e) => ({ ...e, username: "This username is already taken." }));
      } else if (ok && available === true) {
        setErrors((e) => ({ ...e, username: "" }));
      } else {
        // unknown/failed -> don’t block the user; just clear "checking"
        setErrors((e) => ({ ...e, username: "" }));
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [username]);

  /* ---------------- email availability (debounced) ---------------- */
  useEffect(() => {
    const baseErr = validateEmail(email);
    setErrors((e) => ({ ...e, email: baseErr }));
    setEmailAvail({ checking: false, available: null });
    if (baseErr || !email) return;

    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      setEmailAvail({ checking: true, available: null });

      const { ok, data } = await safeCheck("/auth/check-email", { email });
      if (cancelled) return;

      const available = parseAvailable(data);
      setEmailAvail({ checking: false, available });

      if (ok && available === false) {
        setErrors((e) => ({ ...e, email: "An account with this email already exists." }));
      } else if (ok && available === true) {
        setErrors((e) => ({ ...e, email: "" }));
      } else {
        setErrors((e) => ({ ...e, email: "" }));
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [email]);

  /* ---------------- submit ---------------- */
  const handleClick = async (e) => {
    e.preventDefault();

    const newErrors = {
      username: validateUsername(username),
      email: validateEmail(email),
      password: validatePassword(password),
      birthday: validateBirthday(),
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    const payload = {
      username: usernameRef.current.value.trim(),
      email: emailRef.current.value.toLowerCase(),
      password: passwordRef.current.value,
      birthdayMonth: birthdayMonthRef.current.value,
      birthdayDay: birthdayDayRef.current.value,
      birthdayYear: birthdayYearRef.current.value,
    };

    try {
      setProgress(50);
      const res = await axiosInstance.post("/auth/register", payload);
      setProgress(100);
      setMsg(res?.data?.message || "Account created!");

      if (!authUser) {
        await loginCall({ email: payload.email, password: payload.password }, dispatch);
      }
      navigate("/admin");
    } catch (err) {
      setProgress(0);
      const status = err?.response?.status;
      const msgText = err?.response?.data?.message || "Registration failed. Please try again.";
      let uErr = errors.username;
      let eErr = errors.email;

      if (status === 409 || /username/i.test(msgText)) uErr = "This username is already taken.";
      if (status === 409 && /email/i.test(msgText)) eErr = "An account with this email already exists.";

      setErrors((prev) => ({ ...prev, username: uErr, email: eErr }));
      setMsg(msgText);
      console.error(err);
    }
  };

  /* ---------------- UI helpers ---------------- */
  const emailHelp =
    errors.email ||
    (email && (emailAvail.checking ? "" : emailAvail.available === true ? "Email looks good." : ""));

  const usernameHelp =
    errors.username ||
    (username &&
      (usernameAvail.checking ? "" : usernameAvail.available === true ? "Username is available." : ""));

  const canSubmit =
    !errors.username && !errors.email && !errors.password && !errors.birthday && username && email && password;

  return (
    <div className="register">
      <div className="registerWrapper">
        <div className="registerRight">
          <form className="registerBox" onSubmit={handleClick} noValidate>
            <Link to="/" className="logo link-style">
              <h3 className="registerLogo">CLIPBAIT</h3>
            </Link>

            {/* Email */}
            <label className="registerLabel">Email</label>
            <input
              required
              ref={emailRef}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`registerInput ${errors.email ? "input-error" : ""}`}
              type="email"
              placeholder=""
              aria-invalid={!!errors.email}
              aria-describedby="email-help"
            />
            <div id="email-help" className={`help ${errors.email ? "error" : "muted"}`}>{emailHelp}</div>

            {/* Birthday */}
            <label className="registerLabel">Birthday (Month / Day / Year)</label>
            <div className="birthday-wrapper">
              <select ref={birthdayMonthRef} className="birthdayInput" required defaultValue="">
                <option value="">MM</option>
                {months.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <select ref={birthdayDayRef} className="birthdayInput" required defaultValue="">
                <option value="">DD</option>
                {days.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select ref={birthdayYearRef} className="birthdayInput" required defaultValue="">
                <option value="">YYYY</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            {errors.birthday && <div className="help error">{errors.birthday}</div>}

            {/* Username */}
            <label className="registerLabel">Username</label>
            <input
              maxLength={39}
              required
              ref={usernameRef}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={`registerInput ${errors.username ? "input-error" : ""}`}
              placeholder=""
              aria-invalid={!!errors.username}
              aria-describedby="username-help"
            />
            <div id="username-help" className={`help ${errors.username ? "error" : "muted"}`}>{usernameHelp}</div>

            {/* Password */}
            <label className="registerLabel">Password</label>
            <input
              required
              ref={passwordRef}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`registerInput ${errors.password ? "input-error" : ""}`}
              type="password"
              minLength={3}
              aria-invalid={!!errors.password}
              aria-describedby="password-help"
            />
            <div id="password-help" className={`help ${errors.password ? "error" : "muted"}`}>
              {errors.password || "At least 3 characters."}
            </div>

            {msg && <div className="message">{msg}</div>}

            <button className="registerButton" type="submit" disabled={!canSubmit}>
              {canSubmit ? "Sign Up" : "Fix errors to continue"}
            </button>

            <span className="terms">
              By signing up, you agree to our <strong className="highlight">Privacy Policy</strong> and{" "}
              <strong className="highlight">Terms of Use</strong>.
            </span>

            {progress > 0 && (
              <div className="progress-circle-container">
                <CircularProgress color="primary" />
              </div>
            )}

            <span className="registerRegisterButton">
              <Link to="/login" style={{ textDecoration: "none" }}>
                <span className="regbold">Log In</span>
              </Link>
            </span>
          </form>
        </div>
      </div>
    </div>
  );
}
